(function initPhoneVerificationModule() {

  const TOAST_AUTO_HIDE_MS = 5000;

  const isTruthyValue = (value) => ['1', 'true', 'yes'].includes(String(value || '').trim().toLowerCase());

  const state = {
    modal: null,
    removeModal: null,
    promptModal: null,
    promptAutoOpen: false,
    phonePromptIncrementUrl: '',
    promptPopupCount: 0,
    promptPopupMaxCount: 5,
    promptCooldownDays: 30,
    promptIncrementSent: false,
    promptAutoOpenStarted: false,
    promptShowSuppressed: false,
    cards: [],
    customerId: '',
    customerEmail: '',
    shopDomain: '',
    phoneRemoveUrl: '',
    phoneNumber: '',
    phoneCountry: '',
    phoneVerified: false,
    pendingPhoneNumber: '',
    pendingPhoneCountry: '',
    pendingPhoneLocalNumber: '',
    pendingVerificationSid: '',
    verifyLocked: false,
    otpExpired: false,
    verifyInProgress: false,
    resendInProgress: false,
    removeInProgress: false,
    resendTimerId: null,
    toastAutoHideTimerId: null,
    resendSeconds: 30,
    i18n: {}
  };

  const t = (key, fallback = '') => String(state.i18n[key] || fallback || '');

  const buildInvalidOtpMessage = (data) => {
    const template = t(
      'otpInvalidOrExpired',
      'That code is incorrect or has expired. (__ATTEMPTS_LEFT__ attempts left)'
    );
    const attemptsLeft = Number(data?.attemptsRemaining);
    if (!Number.isFinite(attemptsLeft)) return template;
    return template.replace('__ATTEMPTS_LEFT__', String(attemptsLeft));
  };

  const setStep = (stepName) => {
    if (!state.modal) return;
    const steps = state.modal.querySelectorAll('[data-phone-step]');
    for (const step of steps) {
      step.classList.toggle('is-active', step.dataset.phoneStep === stepName);
    }
  };

  const setErrorContent = (element, message) => {
    const value = String(message);
    if (value.includes('<a') || value.includes('<A')) {
      element.innerHTML = value;
      return;
    }
    element.textContent = value;
  };

  const buildPhoneAlreadyInUseMessage = () => {
    const template = document.getElementById('phone-in-use-error-template');
    let html = template?.innerHTML?.trim() || '';

    if (!html) {
      let contactUsUrl = window.KROWN?.settings?.routes?.contact_us || t('contactUsUrl') || '/pages/contact-us';
      const message = t('phoneAlreadyInUse', 'This phone number is already in use on another account. If this is incorrect please [[link]]');
      const linkText = t('phoneAlreadyInUseLink', 'contact us');
      const parts = String(message).split('[[link]]');
      html = `${parts[0] || ''}<a href="${contactUsUrl}">${linkText}</a>${parts[1] || ''}`;
    }

    const partnerId = String(
      new URLSearchParams(window.location.search).get('partner')
      || (typeof getPartnerDetailsFromLocalStorage === 'function' && getPartnerDetailsFromLocalStorage()?.commSysPartnerID)
      || ''
    ).trim();

    if (partnerId) {
      html = html.replace(/<a href="([^"]+)"/, (match, url) => {
        const separator = url.includes('?') ? '&' : '?';
        return `<a href="${url}${separator}partner=${encodeURIComponent(partnerId)}"`;
      });
    }

    return html;
  };

  const bindPhoneVerificationErrorLinks = () => {
    if (!state.modal || state.modal.dataset.phoneErrorLinksReady === 'true') return;

    state.modal.addEventListener('click', (event) => {
      const link = event.target.closest('.phone-verify-modal__error a[href]');
      if (!link || !state.modal.contains(link)) return;

      const href = String(link.getAttribute('href') || '').trim();
      if (!href || href === '#') return;

      event.preventDefault();
      window.location.assign(href);
    });

    state.modal.dataset.phoneErrorLinksReady = 'true';
  };

  const isCodeExpiredOtpMessage = (message, errorType) => errorType === 'expired';

  const showError = (name, message, errorType) => {
    if (!state.modal) return;
    const target = state.modal.querySelector(`[data-phone-${name}-error]`);
    if (!target) return;
    const textEl = target.querySelector('[data-phone-otp-error-text]');
    if (name === 'otp' && textEl) {
      setErrorContent(textEl, message);
      target.classList.remove('is-invalid', 'is-expired', 'is-phone-invalid');
      if (errorType) target.classList.add(`is-${errorType}`);
      if (name === 'otp' && isCodeExpiredOtpMessage(message, errorType)) {
        state.otpExpired = true;
        setOtpVerifyDisabled(true);
      } else if (name === 'otp') {
        state.otpExpired = false;
        if (!state.verifyLocked) {
          setOtpVerifyDisabled(false);
        }
      }
    } else {
      setErrorContent(target, message);
    }
    target.hidden = false;
  };

  const clearError = (name) => {
    if (!state.modal) return;
    const target = state.modal.querySelector(`[data-phone-${name}-error]`);
    if (!target) return;
    const textEl = target.querySelector('[data-phone-otp-error-text]');
    if (textEl) textEl.innerHTML = '';
    else target.innerHTML = '';
    if (name === 'otp') target.classList.remove('is-invalid', 'is-expired', 'is-phone-invalid');
    target.hidden = true;
  };

  const getCountryDialCodes = () => {
    const countries = Array.isArray(window.JP_PHONE_COUNTRIES) ? window.JP_PHONE_COUNTRIES : [];
    const uniqueDialCodes = [];
    for (const country of countries) {
      const dial = String(country?.dial || '').trim();
      if (!dial || uniqueDialCodes.includes(dial)) continue;
      uniqueDialCodes.push(dial);
    }
    return uniqueDialCodes.sort((a, b) => b.length - a.length);
  };

  const inferIsoFromDial = (dial, root = state.modal) => {
    const countries = Array.isArray(window.JP_PHONE_COUNTRIES) ? window.JP_PHONE_COUNTRIES : [];
    const normalizedDial = String(dial || '').trim();
    if (!normalizedDial) return '';
    const matches = countries.filter((country) => String(country?.dial || '').trim() === normalizedDial);
    if (matches.length === 1) {
      return String(matches[0].iso || '').trim().toLowerCase();
    }
    const storeDefaultIso = String(
      root?.querySelector('[data-phone-country-code]')?.dataset?.defaultCountryIso || ''
    )
      .trim()
      .toLowerCase();
    if (matches.length > 1 && storeDefaultIso) {
      const marketMatch = matches.find(
        (country) => String(country.iso || '').trim().toLowerCase() === storeDefaultIso
      );
      if (marketMatch) return storeDefaultIso;
    }
    if (matches.length) return String(matches[0].iso || '').trim().toLowerCase();
    return '';
  };

  const splitE164Phone = (phone) => {
    const normalized = String(phone || '').trim();
    if (!normalized || !/^\+[1-9]\d{1,14}$/.test(normalized)) {
      return { countryCode: '', localNumber: '' };
    }
    const dialCodes = getCountryDialCodes();
    for (const code of dialCodes) {
      if (!normalized.startsWith(code)) continue;
      const localNumber = normalized.slice(code.length).replace(/[^\d]/g, '');
      if (localNumber) {
        return { countryCode: code, localNumber };
      }
    }
    return { countryCode: '', localNumber: normalized.replace(/^\+/, '') };
  };

  const prefillPhoneInputs = (countryCode, localNumber, countryIso, root = state.modal) => {
    if (!root) return;
    const countryCodeInput = root.querySelector('[data-phone-country-code]');
    const localNumberInput = root.querySelector('[data-phone-local-number]');
    if (countryCodeInput && countryCode) {
      countryCodeInput.value = countryCode;
      countryCodeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (localNumberInput) {
      localNumberInput.value = String(localNumber || '').replace(/[^\d]/g, '');
    }
    const hint = String(countryIso || state.phoneCountry || '').trim().toLowerCase();
    const resolvedIso = /^[a-z]{2}$/.test(hint) ? hint : inferIsoFromDial(countryCode);
    if (resolvedIso) {
      root.dispatchEvent(new CustomEvent('phone-verification:set-country-iso', {
        detail: { iso: resolvedIso }
      }));
    }
  };

  const clearCollectStepToMarketDefault = () => {
    if (!state.modal) return;
    const localNumberInput = state.modal.querySelector('[data-phone-local-number]');
    if (localNumberInput) localNumberInput.value = '';
    state.modal.dispatchEvent(new CustomEvent('phone-verification:apply-market-default'));
  };

  const getDisplayPhone = () => String(state.phoneNumber || '').trim();
  const isE164Phone = (value) => /^\+[1-9]\d{1,14}$/.test(String(value || '').trim());

  const formatMaskedPhoneDisplay = (phone) => {
    const lastThree = String(phone || '').replace(/\D/g, '').slice(-3);
    if (lastThree.length < 3) return String(phone || '').trim();
    return `•••• ••• ${lastThree}`;
  };

  const syncCollectSubmit = () => {
    if (!state.modal) return;
    const collectSubmit = state.modal.querySelector('[data-phone-collect-submit]');
    const localNumberInput = state.modal.querySelector('[data-phone-local-number]');
    if (!collectSubmit || !localNumberInput) return;
    const enabled = Boolean(String(localNumberInput.value || '').replace(/[^\d]/g, ''));
    collectSubmit.disabled = !enabled;
    collectSubmit.classList.toggle('disabled', !enabled);
    collectSubmit.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  };

  const showSendCodeLoader = () => {
    const collectSubmit = state.modal?.querySelector('[data-phone-collect-submit]');
    if (!collectSubmit) return;
    collectSubmit.classList.add('working');
    collectSubmit.classList.remove('disabled');
    collectSubmit.disabled = false;
    collectSubmit.setAttribute('aria-disabled', 'true');
    collectSubmit.setAttribute('aria-busy', 'true');
  };

  const hideSendCodeLoader = () => {
    const collectSubmit = state.modal?.querySelector('[data-phone-collect-submit]');
    if (collectSubmit) {
      collectSubmit.classList.remove('working');
      collectSubmit.removeAttribute('aria-busy');
    }
    syncCollectSubmit();
  };

  const showVerifyLoader = () => {
    const otpSubmit = state.modal?.querySelector('[data-phone-otp-submit]');
    if (!otpSubmit) return;
    otpSubmit.classList.add('working');
    otpSubmit.disabled = false;
    otpSubmit.setAttribute('aria-disabled', 'true');
    otpSubmit.setAttribute('aria-busy', 'true');
    setOtpInputsDisabled(true);
  };

  const hideVerifyLoader = () => {
    const otpSubmit = state.modal?.querySelector('[data-phone-otp-submit]');
    if (otpSubmit) {
      otpSubmit.classList.remove('working');
      otpSubmit.removeAttribute('aria-busy');
    }
    setOtpVerifyDisabled(state.verifyLocked || state.otpExpired);
    if (!state.verifyLocked) setOtpInputsDisabled(false);
  };

  const updateCards = () => {
    const displayPhone = getDisplayPhone();
    const hasPhone = Boolean(displayPhone);
    const ctaText = !hasPhone ? t('addNumberCta', 'Add number') : state.phoneVerified ? t('changeCta', 'Change') : t('verifyNumberCta', 'Verify number');
    const removeText = t('removeCta', 'Remove');
    const statusText = !hasPhone
      ? t('statusMissingPhone', 'No phone number added yet')
      : state.phoneVerified
        ? t('statusVerified', 'Verified')
        : t('statusNotVerified', 'Not yet verified');

    const isUnverified = hasPhone && !state.phoneVerified;

    for (const card of state.cards) {
      const phoneDisplay = card.querySelector('[data-phone-display]');
      const statusDisplay = card.querySelector('[data-phone-status-message]');
      const ctaButton = card.querySelector('[data-phone-cta]');
      const removeButton = card.querySelector('[data-phone-remove-cta]');
      const verifiedBadge = card.querySelector('[data-phone-verified-badge]');
      const labelEl = card.querySelector('.account-signin-security__label');
      const helperEl = card.querySelector('.account-signin-security__helper--empty');

      card.classList.toggle('is-empty', !hasPhone);
      card.classList.toggle('is-unverified', isUnverified);
      if (labelEl) {
        if (!hasPhone) {
          labelEl.textContent = t('labelAddMobile', 'Add your mobile number');
        } else if (isUnverified) {
          labelEl.textContent = t('labelUseNumberLogin', 'Use your number to log in');
        } else {
          labelEl.textContent = t('labelMobile', 'Mobile number');
        }
      }
      if (helperEl) {
        if (!hasPhone) {
          helperEl.textContent = t('helperEmpty', 'Sign in more securely with a one-time code.');
          helperEl.hidden = false;
        } else if (isUnverified) {
          helperEl.textContent = t(
            'helperUnverified',
            'This number is saved to your orders. Verify to sign in with it too.'
          );
          helperEl.hidden = false;
        } else {
          helperEl.hidden = true;
        }
      }
      if (phoneDisplay) {
        phoneDisplay.textContent = hasPhone
          ? formatMaskedPhoneDisplay(displayPhone)
          : t('displayMissingPhone', 'Add your phone number');
      }
      if (statusDisplay) {
        statusDisplay.textContent = statusText;
        statusDisplay.classList.toggle('is-verified', hasPhone && state.phoneVerified);
      }
      if (ctaButton) {
        ctaButton.textContent = ctaText;
        ctaButton.classList.toggle('button--solid', !hasPhone);
        ctaButton.classList.toggle('button--outline', isUnverified);
      }
      if (removeButton) {
        removeButton.textContent = removeText;
        removeButton.hidden = !hasPhone;
      }
      if (verifiedBadge) verifiedBadge.hidden = !state.phoneVerified;
    }
    const configNode = document.getElementById('jp-phone-verification-config');
    if (configNode) {
      configNode.dataset.phoneNumber = String(state.phoneNumber || '');
      configNode.dataset.phoneVerified = state.phoneVerified ? 'true' : 'false';
    }
  };

  const clearOtpInputs = () => {
    if (!state.modal) return;
    const otpInputs = state.modal.querySelectorAll('[data-phone-otp-digit]');
    for (const input of otpInputs) {
      input.value = '';
    }
    if (otpInputs[0]) otpInputs[0].focus();
  };

  const setOtpInputsDisabled = (disabled) => {
    if (!state.modal) return;
    const otpInputs = state.modal.querySelectorAll('[data-phone-otp-digit]');
    for (const input of otpInputs) {
      input.disabled = Boolean(disabled);
    }
  };

  const setOtpVerifyDisabled = (disabled) => {
    if (!state.modal) return;
    const verifyButton = state.modal.querySelector('[data-phone-otp-submit]');
    if (!verifyButton) return;
    const isDisabled = Boolean(disabled);
    verifyButton.disabled = isDisabled;
    verifyButton.classList.toggle('disabled', isDisabled);
    verifyButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
  };

  const startResendCountdown = () => {
    if (!state.modal) return;
    const resendButton = state.modal.querySelector('[data-phone-otp-resend-btn]');
    const resendTimer = state.modal.querySelector('[data-phone-otp-resend-timer]');
    if (!resendButton || !resendTimer) return;
    if (state.resendTimerId) {
      clearTimeout(state.resendTimerId);
      state.resendTimerId = null;
    }
    let secondsLeft = state.resendSeconds;
    resendButton.disabled = true;
    const tick = () => {
      resendTimer.textContent = `${t('resendIn', 'Resend in')} ${secondsLeft}s`;
      if (secondsLeft <= 0) {
        resendButton.disabled = state.verifyLocked || state.resendInProgress;
        resendTimer.textContent = t('resendAction', 'Resend');
        return;
      }
      secondsLeft -= 1;
      state.resendTimerId = setTimeout(tick, 1000);
    };
    tick();
  };

  const hideModal = () => {
    if (!state.modal) return;
    if (typeof state.modal.hide === 'function') {
      state.modal.hide();
      return;
    }
    state.modal.style.display = 'none';
  };

  const showModal = () => {
    if (!state.modal) return;
    if (typeof state.modal.show === 'function') {
      state.modal.show();
      return;
    }
    state.modal.style.display = 'block';
  };

  const closeModal = () => {
    hideSendCodeLoader();
    if (state.modal) {
      state.modal.dispatchEvent(new CustomEvent('phone-verification:reset-country-dropdown'));
    }
    hideModal();
  };

  const getPhoneVerifyToast = () => document.querySelector('#jp-phone-verification-toasts .phone-verify-toast');

  const clearToastAutoHideTimer = () => {
    if (!state.toastAutoHideTimerId) return;
    clearTimeout(state.toastAutoHideTimerId);
    state.toastAutoHideTimerId = null;
  };

  const hideSuccessToast = () => {
    clearToastAutoHideTimer();
    const toastPanel = getPhoneVerifyToast();
    if (!toastPanel) return;
    toastPanel.classList.add('toast-hide');
    setTimeout(() => {
      const msg = toastPanel.querySelector('.phone-verify-toast__message');
      if (msg) msg.textContent = '';
      toastPanel.classList.add('display-none');
    }, 200);
  };

  const showSuccessToast = (message) => {
    const toastPanel = getPhoneVerifyToast();
    if (!toastPanel) return;
    clearToastAutoHideTimer();
    const msg = toastPanel.querySelector('.phone-verify-toast__message');
    if (msg) msg.textContent = message;
    toastPanel.classList.remove('display-none');
    setTimeout(() => {
      toastPanel.classList.remove('toast-hide');
    }, 10);
    state.toastAutoHideTimerId = setTimeout(hideSuccessToast, TOAST_AUTO_HIDE_MS);
  };

  const hideRemoveModal = () => {
    if (!state.removeModal) return;
    if (typeof state.removeModal.hide === 'function') {
      state.removeModal.hide();
      return;
    }
    state.removeModal.style.display = 'none';
  };

  const showRemoveModal = () => {
    if (!state.removeModal) return;
    if (typeof state.removeModal.show === 'function') {
      state.removeModal.show();
      return;
    }
    state.removeModal.style.display = 'block';
  };

  const hidePromptModal = () => {
    if (!state.promptModal) return;
    if (typeof state.promptModal.hide === 'function') {
      state.promptModal.hide();
      return;
    }
    state.promptModal.style.display = 'none';
  };

  const showPromptModal = () => {
    if (!state.promptModal) return;
    if (typeof state.promptModal.show === 'function') {
      state.promptModal.show();
      return;
    }
    state.promptModal.style.display = 'block';
  };

  const getMarketDialAndIso = (root = state.modal) => {
    const marketIso = String(
      root?.querySelector('[data-phone-country-code]')?.dataset?.defaultCountryIso || ''
    )
      .trim()
      .toLowerCase();
    const countries = Array.isArray(window.JP_PHONE_COUNTRIES) ? window.JP_PHONE_COUNTRIES : [];
    const marketCountry = countries.find(
      (country) => String(country?.iso || '').trim().toLowerCase() === marketIso
    );
    return {
      iso: marketIso,
      dial: String(marketCountry?.dial || '').trim()
    };
  };

  const normalizeToE164 = (phone, root = state.modal) => {
    const raw = String(phone || '').trim();
    if (!raw) return '';
    if (isE164Phone(raw)) return raw;
    const plusDigits = raw.startsWith('+') ? `+${raw.replace(/\D/g, '')}` : '';
    if (isE164Phone(plusDigits)) return plusDigits;
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    const { dial } = getMarketDialAndIso(root);
    const dialDigits = dial.replace(/\D/g, '');
    if (raw.startsWith('00') && digits.length > 4) {
      const asE164 = `+${digits.replace(/^00/, '')}`;
      if (isE164Phone(asE164)) return asE164;
    }
    if (dialDigits && digits.startsWith(dialDigits) && isE164Phone(`+${digits}`)) {
      return `+${digits}`;
    }
    if (dial && digits.startsWith('0')) {
      const asE164 = `${dial}${digits.slice(1)}`;
      if (isE164Phone(asE164)) return asE164;
    }
    if (dial) {
      const asE164 = `${dial}${digits}`;
      if (isE164Phone(asE164)) return asE164;
    }
    return '';
  };

  const resolvePromptPhone = () => {
    const configNode = document.getElementById('jp-phone-verification-config');
    const candidates = [
      state.phoneNumber,
      configNode?.dataset?.pendingPhone,
      configNode?.dataset?.authPhone
    ];
    for (const candidate of candidates) {
      const normalized = normalizeToE164(candidate, state.promptModal);
      if (normalized) return normalized;
    }
    for (const candidate of candidates) {
      const raw = String(candidate || '').trim();
      if (raw) return raw;
    }
    return '';
  };

  const fillPromptPhoneDisplay = () => {
    if (!state.promptModal) return;
    const ctaTextNode = state.promptModal.querySelector('[data-phone-prompt-cta-text]');
    const errorNode = state.promptModal.querySelector('[data-phone-prompt-error]');
    const e164Phone = resolvePromptPhone();
    if (e164Phone) state.phoneNumber = e164Phone;
    const parsedPhone = splitE164Phone(
      normalizeToE164(e164Phone, state.promptModal) || e164Phone
    );
    const { iso: marketIso } = getMarketDialAndIso(state.promptModal);
    const iso = inferIsoFromDial(parsedPhone.countryCode, state.promptModal) || marketIso;
    prefillPhoneInputs(parsedPhone.countryCode, parsedPhone.localNumber, iso, state.promptModal);
    if (errorNode) {
      errorNode.textContent = '';
      errorNode.hidden = true;
    }
    state.promptModal.querySelector('[data-phone-prompt-field]')?.classList.remove('is-input-error');
    if (ctaTextNode) ctaTextNode.textContent = t('promptVerifyCta', 'Verify my number');
  };

  const startVerifyFromPrompt = () => {
    if (!state.promptModal) return;
    const countryCodeInput = state.promptModal.querySelector('[data-phone-country-code]');
    const localNumberInput = state.promptModal.querySelector('[data-phone-local-number]');
    const errorNode = state.promptModal.querySelector('[data-phone-prompt-error]');
    const promptField = state.promptModal.querySelector('[data-phone-prompt-field]');
    const countryCode = String(countryCodeInput?.value || '').trim();
    const localNumber = String(localNumberInput?.value || '').replace(/[^\d]/g, '');
    const fullPhone = `${countryCode}${localNumber}`;
    const isValidCountryCode = /^\+[1-9]\d{0,3}$/.test(countryCode);
    const isValidLocalNumber = /^\d{4,14}$/.test(localNumber);

    if (errorNode) {
      errorNode.textContent = '';
      errorNode.hidden = true;
    }
    promptField?.classList.remove('is-input-error');
    if (!isValidCountryCode || !isValidLocalNumber || !isE164Phone(fullPhone)) {
      if (errorNode) {
        errorNode.textContent = t('invalidE164', 'Enter a valid phone number');
        errorNode.hidden = false;
      }
      promptField?.classList.add('is-input-error');
      return;
    }

    const verifyButton = state.promptModal.querySelector('[data-phone-prompt-verify]');
    if (verifyButton) {
      verifyButton.classList.add('working');
      verifyButton.setAttribute('aria-busy', 'true');
    }

    fetch('/apps/otp/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        email: state.customerEmail,
        shop: state.shopDomain,
        phoneNumber: fullPhone,
        channel: 'sms'
      })
    })
      .then((response) => response.json().catch(() => ({})).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        const status = String(data?.status || '').trim().toUpperCase();
        if (status === 'OTP_SENT' && ok !== false) {
          hidePromptModal();
          state.phoneNumber = fullPhone;
          state.phoneCountry = inferIsoFromDial(countryCode, state.promptModal);
          state.pendingPhoneCountry = countryCode;
          state.pendingPhoneLocalNumber = localNumber;
          state.pendingPhoneNumber = fullPhone;
          updateCards();
          const responseChannel = String(data?.channel || 'sms').trim().toLowerCase();
          const verificationSid = String(data?.verificationSid || '').trim();
          state.pendingVerificationSid = responseChannel === 'sms' && verificationSid ? verificationSid : '';
          clearError('collect');
          clearError('otp');
          const previewNode = state.modal?.querySelector('[data-phone-preview]');
          if (previewNode) previewNode.textContent = formatMaskedPhoneDisplay(fullPhone);
          clearOtpInputs();
          setOtpInputsDisabled(false);
          setOtpVerifyDisabled(false);
          setStep('otp');
          showModal();
          startResendCountdown();
          return;
        }

        hideModal();
        showPromptModal();
        let promoErrorMessage = String(data?.message || data?.error || '').trim()
          || t('requestUnavailable', 'Unable to send code right now. Please try again.');
        if (status === 'PHONE_INVALID' || status === 'PHONE_FORMAT_INVALID') {
          promoErrorMessage = t('phoneInvalid', 'This phone number can not be used.\nPlease try again');
        } else if (status === 'PHONE_ALREADY_IN_USE') {
          promoErrorMessage = buildPhoneAlreadyInUseMessage();
        } else if (status === 'RATE_LIMIT_EXCEEDED' || status === 'OTP_COOLDOWN') {
          promoErrorMessage = t('retryLater', 'Please wait before trying again');
        }
        if (errorNode) {
          setErrorContent(errorNode, promoErrorMessage);
          errorNode.hidden = false;
        }
        promptField?.classList.add('is-input-error');
      })
      .catch(() => {
        hideModal();
        showPromptModal();
        if (errorNode) {
          errorNode.textContent = t('requestUnavailable', 'Unable to send code right now. Please try again.');
          errorNode.hidden = false;
        }
        promptField?.classList.add('is-input-error');
      })
      .finally(() => {
        if (verifyButton) {
          verifyButton.classList.remove('working');
          verifyButton.removeAttribute('aria-busy');
        }
      });
  };

  const isPromoCooldownElapsed = (lastDisplayedValue) => {
    const raw = String(lastDisplayedValue || '').trim();
    if (!raw) return true;
    const lastTs = Date.parse(raw);
    if (Number.isNaN(lastTs)) return true;
    const cooldownDays = Number(state.promptCooldownDays || 30) || 30;
    return (Date.now() - lastTs) / 1000 >= cooldownDays * 86400;
  };

  // The backend clears auth.last_displayed_popup_at on a successful verify and
  // on phone/remove. The jp_pv_last_ cookie mirrors that metafield (see below)
  // and is read as a fallback whenever it is empty, so it must be cleared at
  // the same points or it keeps suppressing the prompt in this browser for the
  // rest of the cooldown.
  const clearPromptCooldownCookie = () => {
    if (!state.customerId) return;
    document.cookie = `jp_pv_last_${state.customerId}=; max-age=0; path=/; SameSite=Lax`;
  };

  const incrementPromptPopupDisplay = () => {
    if (!state.phonePromptIncrementUrl || state.promptIncrementSent) return Promise.resolve();
    if (!state.shopDomain) return Promise.resolve();
    state.promptIncrementSent = true;
    return fetch(state.phonePromptIncrementUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        shop: state.shopDomain
      })
    })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        const serverCount = Number(data?.displayPopupCount);
        if (Number.isFinite(serverCount)) state.promptPopupCount = serverCount;
        // Bridge the Shopify metafield propagation delay (~200-800ms) so a quick
        // refresh doesn't re-show the popup before last_displayed_popup_at is visible
        // in Liquid. Scoped by customerId to prevent cross-user contamination.
        if (state.customerId) {
          const cooldownSeconds = (Number(state.promptCooldownDays || 30) || 30) * 86400;
          document.cookie = `jp_pv_last_${state.customerId}=${encodeURIComponent(new Date().toISOString())}; max-age=${cooldownSeconds}; path=/; SameSite=Lax`;
        }
      })
      .catch(() => {});
  };

  const maybeAutoOpenPrompt = () => {
    if (!state.promptAutoOpen || !state.promptModal || state.promptAutoOpenStarted) return;
    if (state.phoneVerified) return;
    if (state.promptPopupCount >= state.promptPopupMaxCount) return;
    const configNode = document.getElementById('jp-phone-verification-config');
    let serverLastDisplayed = configNode?.dataset?.promptLastDisplayed || '';
    // If Liquid hasn't propagated the metafield yet (race condition after /increment),
    // fall back to the cookie written when /increment last committed.
    if (!serverLastDisplayed && state.customerId) {
      const cookieKey = `jp_pv_last_${state.customerId}=`;
      const cookieEntry = document.cookie.split('; ').find((c) => c.startsWith(cookieKey));
      if (cookieEntry) serverLastDisplayed = decodeURIComponent(cookieEntry.slice(cookieKey.length));
    }
    if (!isPromoCooldownElapsed(serverLastDisplayed)) return;
    state.promptAutoOpenStarted = true;
    const openPrompt = () => {
      const promptPhone = resolvePromptPhone();
      const verifiedAuthPhone = Boolean(
        state.phoneVerified && String(configNode?.dataset?.authPhone || '').trim()
      );
      if (verifiedAuthPhone) return;
      if (promptPhone) state.phoneNumber = promptPhone;
      // Commit the increment first so last_displayed_popup_at is written before the
      // popup is visible — prevents a quick refresh from racing the metafield write.
      // The popup still shows if the request fails (via .finally).
      incrementPromptPopupDisplay().finally(() => {
        if (state.promptShowSuppressed) return;
        showPromptModal();
        try {
          window.JPPhoneCountryUi?.ensurePromptCountryPicker?.();
        } catch (_) {}
        fillPromptPhoneDisplay();
      });
    };
    if (Array.isArray(window.JP_PHONE_COUNTRIES) && window.JP_PHONE_COUNTRIES.length) {
      openPrompt();
      return;
    }
    let attempts = 0;
    const timerId = setInterval(() => {
      attempts += 1;
      if ((Array.isArray(window.JP_PHONE_COUNTRIES) && window.JP_PHONE_COUNTRIES.length) || attempts >= 20) {
        clearInterval(timerId);
        openPrompt();
      }
    }, 50);
  };

  const clearRemoveError = () => {
    if (!state.removeModal) return;
    const errorNode = state.removeModal.querySelector('[data-phone-remove-error]');
    if (!errorNode) return;
    errorNode.textContent = '';
    errorNode.hidden = true;
  };

  const showRemoveError = (message) => {
    if (!state.removeModal) return;
    const errorNode = state.removeModal.querySelector('[data-phone-remove-error]');
    if (!errorNode) return;
    errorNode.textContent = message;
    errorNode.hidden = false;
  };

  const setRemoveStep = (stepName) => {
    if (!state.removeModal) return;
    const steps = state.removeModal.querySelectorAll('[data-phone-remove-step]');
    for (const step of steps) {
      step.classList.toggle('is-active', step.dataset.phoneRemoveStep === stepName);
    }
  };

  const showRemoveLoader = () => {
    const confirmButton = state.removeModal?.querySelector('[data-phone-remove-confirm]');
    if (!confirmButton) return;
    confirmButton.classList.add('working');
    confirmButton.disabled = false;
    confirmButton.setAttribute('aria-disabled', 'true');
    confirmButton.setAttribute('aria-busy', 'true');
  };

  const hideRemoveLoader = () => {
    const confirmButton = state.removeModal?.querySelector('[data-phone-remove-confirm]');
    if (!confirmButton) return;
    confirmButton.classList.remove('working');
    confirmButton.removeAttribute('aria-disabled');
    confirmButton.removeAttribute('aria-busy');
  };

  const openRemoveModal = () => {
    if (!state.removeModal || !state.phoneNumber) return;
    clearRemoveError();
    hideRemoveLoader();
    setRemoveStep('confirm');
    showRemoveModal();
  };

  const openModal = (options = {}) => {
    if (!state.modal) return;
    state.promptShowSuppressed = true;
    hidePromptModal();
    const titleNode = state.modal.querySelector('#phone-verification-title');
    const isVerifyFlow = Boolean(state.phoneNumber) && !state.phoneVerified;

    if (titleNode) {
      titleNode.textContent = t('modalTitleChange', 'Enter your mobile');
    }

    clearError('collect');
    clearError('otp');

    if (isVerifyFlow) {
      const e164Phone = String(state.phoneNumber || '').trim();
      const parsedPhone = splitE164Phone(e164Phone);
      const previewNode = state.modal.querySelector('[data-phone-preview]');
      if (previewNode) previewNode.textContent = formatMaskedPhoneDisplay(e164Phone);
      state.verifyLocked = false;
      state.otpExpired = false;
      state.verifyInProgress = false;
      state.pendingPhoneCountry = parsedPhone.countryCode;
      state.pendingPhoneLocalNumber = parsedPhone.localNumber;
      state.pendingPhoneNumber = e164Phone;
      clearOtpInputs();
      setOtpInputsDisabled(true);
      setOtpVerifyDisabled(true);
      setStep('otp');
      showModal();
      showVerifyLoader();
      if (!isE164Phone(e164Phone)) {
        hideVerifyLoader();
        setOtpInputsDisabled(true);
        setOtpVerifyDisabled(true);
        showError('otp', t('invalidE164', 'Enter a valid phone number in E.164 format'), 'invalid');
        return;
      }
      requestPhoneOtp({
        countryCode: parsedPhone.countryCode,
        localNumber: parsedPhone.localNumber,
        fullPhone: e164Phone,
        isResend: false,
        forceOtpErrorTarget: true
      });
      return;
    }

    setOtpInputsDisabled(false);
    setOtpVerifyDisabled(false);

    clearCollectStepToMarketDefault();
    syncCollectSubmit();
    setStep('collect');
    showModal();
    state.modal.dispatchEvent(new CustomEvent('phone-verification:reset-country-dropdown'));
  };

  const collectPhoneNumber = () => {
    if (!state.modal) return;
    const countryCodeInput = state.modal.querySelector('[data-phone-country-code]');
    const localNumberInput = state.modal.querySelector('[data-phone-local-number]');
    if (!countryCodeInput || !localNumberInput) return;

    const countryCode = String(countryCodeInput.value || '').trim();
    const localNumber = String(localNumberInput.value || '').replace(/[^\d]/g, '');
    const fullPhone = `${countryCode}${localNumber}`;

    const isValidCountryCode = /^\+[1-9]\d{0,3}$/.test(countryCode);
    const isValidLocalNumber = /^\d{4,14}$/.test(localNumber);
    const isValidE164 = /^\+[1-9]\d{1,14}$/.test(fullPhone);
    if (!isValidCountryCode || !isValidLocalNumber || !isValidE164) {
      showError('collect', t('invalidE164', 'Enter a valid phone number in E.164 format'));
      return;
    }
    showSendCodeLoader();
    requestPhoneOtp({
      countryCode,
      localNumber,
      fullPhone,
      isResend: false
    });
  };

  const requestPhoneOtp = ({ countryCode, localNumber, fullPhone, isResend, forceOtpErrorTarget = false }) => {
    if (!state.modal) return;
    const stayOnOtp = isResend || forceOtpErrorTarget;
    const errorTarget = stayOnOtp ? 'otp' : 'collect';
    const otpErrorStyle = errorTarget === 'otp' ? 'invalid' : undefined;
    let requestSucceeded = false;
    if (isResend) {
      state.resendInProgress = true;
      const resendButton = state.modal.querySelector('[data-phone-otp-resend-btn]');
      if (resendButton) resendButton.disabled = true;
    }
    clearError(errorTarget);
    fetch('/apps/otp/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        email: state.customerEmail,
        shop: state.shopDomain,
        phoneNumber: fullPhone,
        channel: 'sms'
      })
    })
      .then((response) => response.json().catch(() => ({})).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        const status = String(data?.status || '').trim().toUpperCase();
        if (status === 'OTP_SENT') {
          const responseChannel = String(data?.channel || 'sms').trim().toLowerCase();
          const verificationSid = String(data?.verificationSid || '').trim();
          state.pendingPhoneCountry = countryCode;
          state.pendingPhoneLocalNumber = localNumber;
          state.pendingPhoneNumber = fullPhone;
          state.pendingVerificationSid = responseChannel === 'sms' && verificationSid ? verificationSid : '';
          // Server saves the number as pending on OTP_SENT; reflect it on the account card
          // now so it doesn't require a refresh. Keep an existing verified number as-is.
          if (!state.phoneVerified) {
            state.phoneNumber = fullPhone;
            state.phoneCountry = inferIsoFromDial(countryCode) || state.phoneCountry;
            updateCards();
          }
          state.verifyLocked = false;
          state.otpExpired = false;
          setOtpInputsDisabled(false);
          setOtpVerifyDisabled(false);
          const previewNode = state.modal.querySelector('[data-phone-preview]');
          if (previewNode) previewNode.textContent = formatMaskedPhoneDisplay(fullPhone);
          setStep('otp');
          clearOtpInputs();
          requestSucceeded = true;
          if (!isResend) startResendCountdown();
          return;
        }

        if (status === 'PHONE_INVALID' || status === 'PHONE_FORMAT_INVALID') {
          showError(
            errorTarget,
            t('phoneInvalid', 'This phone number can not be used.\nPlease try again'),
            'phone-invalid'
          );
          return;
        }

        if (status === 'PHONE_ALREADY_IN_USE') {
          showError(errorTarget, buildPhoneAlreadyInUseMessage());
          return;
        }

        if (status === 'RATE_LIMIT_EXCEEDED' || status === 'OTP_COOLDOWN') {
          showError(errorTarget, t('retryLater', 'Please wait before trying again'), 'invalid');
          return;
        }

        if (!ok || status === 'OTP_SERVICE_UNAVAILABLE' || status === 'OTP_DELIVERY_FAILED') {
          showError(
            errorTarget,
            t('requestUnavailable', 'Unable to send code right now. Please try again.'),
            otpErrorStyle
          );
          return;
        }

        showError(
          errorTarget,
          String(data?.message || data?.error || '').trim() || t('requestUnavailable', 'Unable to send code right now. Please try again.'),
          otpErrorStyle
        );
      })
      .catch(() => {
        showError(
          errorTarget,
          t('requestUnavailable', 'Unable to send code right now. Please try again.'),
          otpErrorStyle
        );
      })
      .finally(() => {
        if (forceOtpErrorTarget) {
          hideVerifyLoader();
          if (!requestSucceeded) {
            setOtpInputsDisabled(true);
            setOtpVerifyDisabled(true);
          }
        } else if (!isResend) {
          hideSendCodeLoader();
        }
        if (!isResend) return;
        state.resendInProgress = false;
        startResendCountdown();
      });
  };

  const removePhoneNumber = async () => {
    if (!state.phoneRemoveUrl || state.removeInProgress) return;
    state.removeInProgress = true;
    clearRemoveError();
    showRemoveLoader();
    try {
      const removeUrl = new URL(state.phoneRemoveUrl, window.location.origin);
      const response = await fetch(removeUrl.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          shop: state.shopDomain
        })
      });
      const data = await response.json().catch(() => ({}));
      const status = String(data?.status || '').trim().toUpperCase();
      if (status === 'PHONE_REMOVED' || status === 'NO_PHONE_ON_FILE') {
        if (status === 'PHONE_REMOVED') {
          // Backend deleted display_popup_count and last_displayed_popup_at.
          clearPromptCooldownCookie();
          state.promptPopupCount = 0;
        }
        state.phoneNumber = '';
        state.phoneCountry = '';
        state.phoneVerified = false;
        state.pendingPhoneNumber = '';
        state.pendingPhoneCountry = '';
        state.pendingPhoneLocalNumber = '';
        state.pendingVerificationSid = '';
        const configNode = document.getElementById('jp-phone-verification-config');
        if (configNode) {
          configNode.dataset.phoneNumber = '';
          configNode.dataset.phoneCountry = '';
        }
        updateCards();
        state.modal?.dispatchEvent(new CustomEvent('phone-verification:apply-market-default'));
        setRemoveStep('confirm');
        hideRemoveModal();
        showSuccessToast(t('removeSuccessTitle', 'Phone number removed'));
        return;
      }
      const message = String(data?.message || data?.error || '').trim();
      showRemoveError(message || t('removeFailed', 'Unable to remove phone number right now. Please try again.'));
    } catch (error) {
      showRemoveError(t('removeFailed', 'Unable to remove phone number right now. Please try again.'));
    } finally {
      state.removeInProgress = false;
      hideRemoveLoader();
    }
  };

  const verifyOtp = () => {
    if (!state.modal) return;
    if (state.verifyLocked || state.verifyInProgress || state.otpExpired) return;
    const otpInputs = state.modal.querySelectorAll('[data-phone-otp-digit]');
    const otpCode = Array.from(otpInputs).map((input) => String(input.value || '').trim()).join('');
    if (!otpCode || otpCode.length !== 6) {
      showError('otp', t('otpRequired', 'Enter the 6-digit code'), 'invalid');
      return;
    }

    clearError('otp');
    state.verifyInProgress = true;
    showVerifyLoader();
    fetch('/apps/otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify((() => {
        const payload = {
        email: state.customerEmail,
        otp: otpCode,
        phoneNumber: state.pendingPhoneNumber,
        channel: 'sms'
        };
        if (state.pendingVerificationSid) {
          payload.verificationSid = state.pendingVerificationSid;
        }
        return payload;
      })())
    })
      .then((response) => response.json().catch(() => ({})).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        const status = String(data?.status || data?.error || '').trim().toUpperCase();
        if (status === 'PHONE_VERIFIED') {
          // Backend reset display_popup_count to 0 and deleted last_displayed_popup_at.
          clearPromptCooldownCookie();
          state.promptPopupCount = 0;
          state.phoneNumber = state.pendingPhoneNumber;
          state.phoneCountry =
            inferIsoFromDial(state.pendingPhoneCountry) || state.pendingPhoneCountry;
          state.phoneVerified = true;
          updateCards();
          setStep('success');
          return;
        }

        if (
          status === 'OTP_INVALID_OR_EXPIRED' ||
          status === 'OTP_INVALID' ||
          status === 'INVALID_OTP'
        ) {
          showError('otp', buildInvalidOtpMessage(data), 'invalid');
          return;
        }

        if (status === 'OTP_LOCKED') {
          state.verifyLocked = true;
          showError('otp', t('otpLocked', 'Too many attempts. Try again later'), 'invalid');
          setOtpInputsDisabled(true);
          setOtpVerifyDisabled(true);
          const resendButton = state.modal.querySelector('[data-phone-otp-resend-btn]');
          if (resendButton) resendButton.disabled = true;
          return;
        }

        if (status === 'PENDING_VERIFICATION_EXPIRED' || status === 'OTP_EXPIRED') {
          showError('otp', t('otpPendingExpired', 'Code expired. Please try again'), 'expired');
          return;
        }

        if (status === 'PHONE_ALREADY_IN_USE') {
          showError('otp', buildPhoneAlreadyInUseMessage());
          return;
        }

        if (!ok || status === 'OTP_SERVICE_UNAVAILABLE') {
          showError('otp', t('verifyUnavailable', 'Unable to verify code right now. Please try again.'), 'invalid');
          return;
        }

        showError(
          'otp',
          String(data?.message || data?.error || '').trim() || t('verifyUnavailable', 'Unable to verify code right now. Please try again.'),
          'invalid'
        );
      })
      .catch(() => {
        showError('otp', t('verifyUnavailable', 'Unable to verify code right now. Please try again.'), 'invalid');
      })
      .finally(() => {
        state.verifyInProgress = false;
        hideVerifyLoader();
        const otpErrorEl = state.modal?.querySelector('[data-phone-otp-error]:not([hidden])');
        if (otpErrorEl?.classList.contains('is-expired')) {
          state.otpExpired = true;
          setOtpVerifyDisabled(true);
        }
      });
  };

  const resendOtp = () => {
    if (!state.pendingPhoneCountry || !state.pendingPhoneLocalNumber || state.verifyLocked) return;
    requestPhoneOtp({
      countryCode: state.pendingPhoneCountry,
      localNumber: state.pendingPhoneLocalNumber,
      fullPhone: state.pendingPhoneNumber,
      isResend: true
    });
  };

  const goBackToCollectStep = () => {
    clearError('otp');
    clearOtpInputs();
    state.verifyLocked = false;
    state.otpExpired = false;
    state.verifyInProgress = false;
    hideVerifyLoader();
    setOtpInputsDisabled(false);
    setOtpVerifyDisabled(false);
    if (state.resendTimerId) {
      clearTimeout(state.resendTimerId);
      state.resendTimerId = null;
    }
    const resendButton = state.modal?.querySelector('[data-phone-otp-resend-btn]');
    if (resendButton) resendButton.disabled = false;
    const titleNode = state.modal.querySelector('#phone-verification-title');
    if (titleNode) {
      titleNode.textContent = t('modalTitleChange', 'Enter your mobile');
    }
    clearCollectStepToMarketDefault();
    syncCollectSubmit();
    setStep('collect');
    state.modal?.dispatchEvent(new CustomEvent('phone-verification:reset-country-dropdown'));
  };

  const openCollectStepFromRemoveModal = () => {
    if (!state.modal) return;
    hideRemoveModal();
    clearRemoveError();
    clearError('collect');
    clearError('otp');
    clearOtpInputs();
    state.verifyLocked = false;
    state.otpExpired = false;
    state.verifyInProgress = false;
    hideVerifyLoader();
    hideSendCodeLoader();
    setOtpInputsDisabled(false);
    setOtpVerifyDisabled(false);
    if (state.resendTimerId) {
      clearTimeout(state.resendTimerId);
      state.resendTimerId = null;
    }
    const resendButton = state.modal.querySelector('[data-phone-otp-resend-btn]');
    if (resendButton) resendButton.disabled = false;
    const titleNode = state.modal.querySelector('#phone-verification-title');
    if (titleNode) {
      titleNode.textContent = t('modalTitleChange', 'Enter your mobile');
    }
    clearCollectStepToMarketDefault();
    syncCollectSubmit();
    setStep('collect');
    showModal();
    state.modal.dispatchEvent(new CustomEvent('phone-verification:reset-country-dropdown'));
  };

  const getJoinedOtpCode = () => {
    if (!state.modal) return '';
    return Array.from(state.modal.querySelectorAll('[data-phone-otp-digit]'))
      .map((cell) => String(cell.value || '').trim())
      .join('');
  };

  // Spread a multi-digit string across the boxes starting at startIndex, focus the
  // box after the last one filled, and verify once all six are populated. Shared by
  // the paste handler and by autofill, which arrives as a plain `input` event.
  const fillOtpDigitsFrom = (startIndex, digits) => {
    if (!state.modal || !digits) return;
    const otpInputs = Array.from(state.modal.querySelectorAll('[data-phone-otp-digit]'));
    clearError('otp');
    for (let j = 0; j < digits.length && startIndex + j < otpInputs.length; j++) {
      otpInputs[startIndex + j].value = digits.charAt(j);
    }
    const focusIndex = Math.min(startIndex + digits.length, otpInputs.length - 1);
    const focusEl = otpInputs[focusIndex];
    if (focusEl) focusEl.focus();
    if (getJoinedOtpCode().length === 6) {
      verifyOtp();
    }
  };

  const bindOtpInputs = () => {
    if (!state.modal) return;
    const otpInputs = Array.from(state.modal.querySelectorAll('[data-phone-otp-digit]'));
    for (const [index, input] of otpInputs.entries()) {
      input.addEventListener('input', () => {
        clearError('otp');
        const raw = String(input.value || '').replace(/[^\d]/g, '');

        // SMS autofill (iOS QuickType, Chrome autofill) drops the whole code into the
        // focused box and fires `input` — never `paste`. Safari spreads it across the
        // six inputs itself; Chrome does not, so truncating to one digit here is what
        // left only the first digit filled. Spread it instead.
        if (raw.length > 1) {
          fillOtpDigitsFrom(index, raw.slice(0, otpInputs.length - index));
          return;
        }

        input.value = raw.slice(0, 1);
        if (input.value && otpInputs[index + 1]) otpInputs[index + 1].focus();
        if (getJoinedOtpCode().length === 6) {
          verifyOtp();
        }
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Backspace' && !input.value && otpInputs[index - 1]) {
          otpInputs[index - 1].focus();
        }
      });
      input.addEventListener('paste', (event) => {
        const paste = (event.clipboardData || window.clipboardData).getData('text') || '';
        const digits = paste.replace(/[^\d]/g, '').slice(0, otpInputs.length - index);
        if (!digits) return;
        event.preventDefault();
        fillOtpDigitsFrom(index, digits);
      });
    }
  };

  const bootstrap = () => {
    const configNode = document.getElementById('jp-phone-verification-config');
    const i18nNode = document.getElementById('jp-phone-verification-i18n');
    state.modal = document.getElementById('phone-verification-modal');
    state.removeModal = document.getElementById('phone-remove-modal');
    state.promptModal = document.getElementById('phone-verification-prompt-modal');
    if (!configNode || !state.modal) return;

    state.customerId = String(configNode.dataset.customerId || '').trim();
    if (!state.customerId) return;
    if (i18nNode) {
      try {
        state.i18n = JSON.parse(i18nNode.textContent || '{}') || {};
      } catch (e) {
        state.i18n = {};
      }
    }
    state.customerEmail = String(configNode.dataset.customerEmail || '').trim();
    state.shopDomain = String(configNode.dataset.shopDomain || '').trim();
    state.phoneRemoveUrl = String(
      window.KROWN &&
      window.KROWN.settings &&
      window.KROWN.settings.login_otp &&
      window.KROWN.settings.login_otp.phone_remove_url
        ? window.KROWN.settings.login_otp.phone_remove_url
        : ''
    ).trim();
    state.phonePromptIncrementUrl = String(
      window.KROWN &&
      window.KROWN.settings &&
      window.KROWN.settings.login_otp &&
      window.KROWN.settings.login_otp.phone_prompt_increment_url
        ? window.KROWN.settings.login_otp.phone_prompt_increment_url
        : '/apps/otp/customer/phone-prompt/increment'
    ).trim();
    if (!state.shopDomain && window.Shopify && window.Shopify.shop) {
      state.shopDomain = String(window.Shopify.shop || '').trim();
    }

    state.phoneVerified = isTruthyValue(configNode.dataset.phoneVerified);
    state.phoneNumber = String(configNode.dataset.phoneNumber || '').trim();
    state.phoneCountry = String(configNode.dataset.phoneCountry || '').trim();
    state.promptAutoOpen = isTruthyValue(configNode.dataset.promptAutoOpen);
    state.promptPopupCount = Number(configNode.dataset.promptPopupCount || 0) || 0;
    state.promptPopupMaxCount = Number(configNode.dataset.promptPopupMaxCount || 5) || 5;
    state.promptCooldownDays = Number(configNode.dataset.promptCooldownDays || 30) || 30;

    state.cards = Array.from(document.querySelectorAll('[data-phone-security-card]'));

    updateCards();
    bindOtpInputs();
    bindPhoneVerificationErrorLinks();

    for (const card of state.cards) {
      const actionButton = card.querySelector('[data-phone-cta]');
      const removeButton = card.querySelector('[data-phone-remove-cta]');
      if (actionButton) actionButton.addEventListener('click', openModal);
      if (removeButton) removeButton.addEventListener('click', openRemoveModal);
    }

    const collectSubmit = state.modal.querySelector('[data-phone-collect-submit]');
    const localNumberInput = state.modal.querySelector('[data-phone-local-number]');
    const otpSubmit = state.modal.querySelector('[data-phone-otp-submit]');
    const otpBack = state.modal.querySelector('[data-phone-otp-back]');
    const otpWrongNumber = state.modal.querySelector('[data-phone-otp-wrong-number]');
    const otpResend = state.modal.querySelector('[data-phone-otp-resend-btn]');

    if (localNumberInput) {
      localNumberInput.addEventListener('input', () => {
        localNumberInput.value = String(localNumberInput.value || '').replace(/[^\d]/g, '').slice(0, 14);
        syncCollectSubmit();
      });
    }
    if (collectSubmit) collectSubmit.addEventListener('click', collectPhoneNumber);
    if (otpSubmit) otpSubmit.addEventListener('click', verifyOtp);
    if (otpBack) otpBack.addEventListener('click', goBackToCollectStep);
    if (otpWrongNumber) otpWrongNumber.addEventListener('click', goBackToCollectStep);
    if (otpResend) {
      otpResend.addEventListener('click', resendOtp);
    }

    const closeNodes = state.modal.querySelectorAll('[data-js-close]');
    for (const closeNode of closeNodes) {
      closeNode.addEventListener('click', closeModal);
    }
    const phoneVerifyToastClose = document.querySelector('[data-phone-verify-toast-close]');
    if (phoneVerifyToastClose) phoneVerifyToastClose.addEventListener('click', hideSuccessToast);

    if (state.removeModal) {
      const removeCloseNodes = state.removeModal.querySelectorAll('[data-phone-remove-close]');
      for (const closeNode of removeCloseNodes) {
        closeNode.addEventListener('click', hideRemoveModal);
      }
      const removeConfirmButton = state.removeModal.querySelector('[data-phone-remove-confirm]');
      if (removeConfirmButton) removeConfirmButton.addEventListener('click', removePhoneNumber);
      const removeUpdateNumberButton = state.removeModal.querySelector('[data-phone-remove-update-number]');
      if (removeUpdateNumberButton) {
        removeUpdateNumberButton.addEventListener('click', openCollectStepFromRemoveModal);
      }
    }

    if (state.promptModal) {
      if (state.promptModal.dataset.phonePromptBackdropBound !== 'true') {
        state.promptModal.dataset.phonePromptBackdropBound = 'true';
        state.promptModal.addEventListener('click', (event) => {
          if (event.target.closest('.modal-content')) return;
          hidePromptModal();
        });
      }
      const promptSkipNodes = state.promptModal.querySelectorAll('[data-phone-prompt-skip]');
      for (const skipNode of promptSkipNodes) {
        skipNode.addEventListener('click', hidePromptModal);
      }
      const promptVerify = state.promptModal.querySelector('[data-phone-prompt-verify]');
      if (promptVerify) promptVerify.addEventListener('click', startVerifyFromPrompt);
      const promptLocalNumber = state.promptModal.querySelector('[data-phone-local-number]');
      if (promptLocalNumber) {
        promptLocalNumber.addEventListener('input', () => {
          promptLocalNumber.value = String(promptLocalNumber.value || '').replace(/[^\d]/g, '').slice(0, 14);
          const errorNode = state.promptModal.querySelector('[data-phone-prompt-error]');
          if (errorNode) {
            errorNode.textContent = '';
            errorNode.hidden = true;
          }
          state.promptModal.querySelector('[data-phone-prompt-field]')?.classList.remove('is-input-error');
        });
      }
      try {
        window.JPPhoneCountryUi?.ensurePromptCountryPicker?.();
      } catch (_) {}
    }

    syncCollectSubmit();
    maybeAutoOpenPrompt();

    window.JPPhoneVerificationModal = {
      open: openModal,
      close: closeModal,
      getState: () => ({
        phoneNumber: state.phoneNumber,
        phoneCountry: state.phoneCountry,
        phoneVerified: state.phoneVerified
      })
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
